import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { CreateContactsPicker } from "../ContactsPicker";
import { CreatePropertiesPicker } from "../PropertiesPicker";
import { CreateClaimsPicker } from "../ClaimsPicker";
import { apiClient } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { CreateOrganizationRequest, Organization } from "@/api/generated/client/models";
import { normalizeWebsite, isValidWebsite } from "@/lib/form-utils";

const EMPTY_FORM: CreateOrganizationRequest = {
    orgType: "" as CreateOrganizationRequest["orgType"],
    name: "",
};

interface OrganizationCreateDialogProps {
    open: boolean;
    onClose: () => void;
    createOrganization: (data: CreateOrganizationRequest) => Promise<Organization>;
    isCreating: boolean;
    /** When set, newly created organization is auto-linked to these claims. */
    initialClaimIds?: number[];
    /** Hide the in-dialog claims picker (used when opened from a claim context). */
    hideClaimsPicker?: boolean;
    /** Called after a successful create (and link attempts). */
    onCreated?: () => void;
}

export function OrganizationCreateDialog({ open, onClose, createOrganization, initialClaimIds, hideClaimsPicker, onCreated }: OrganizationCreateDialogProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [form, setForm] = useState<CreateOrganizationRequest>({ ...EMPTY_FORM });
    const [pendingContactIds, setPendingContactIds] = useState<number[]>([]);
    const [pendingPropertyIds, setPendingPropertyIds] = useState<number[]>([]);
    const [pendingClaimIds, setPendingClaimIds] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Seed claim links when opened from a claim context.
    useEffect(() => {
        if (open && initialClaimIds && initialClaimIds.length > 0) {
            setPendingClaimIds(initialClaimIds);
        }
    }, [open, initialClaimIds]);

    const handleClose = () => {
        onClose();
        setPendingContactIds([]);
        setPendingPropertyIds([]);
        setPendingClaimIds([]);
        setForm({ ...EMPTY_FORM });
    };

    const handleCreate = async () => {
        const normalized = { ...form, website: normalizeWebsite(form.website) };
        if (!isValidWebsite(normalized.website)) return;
        setIsSubmitting(true);
        try {
            const created = await createOrganization(normalized);
            const orgId = created?.id;
            const hasLinks = pendingContactIds.length > 0 || pendingPropertyIds.length > 0 || pendingClaimIds.length > 0;
            const linkFailures: string[] = [];
            if (orgId && pendingContactIds.length > 0) {
                for (const contactId of pendingContactIds) {
                    try {
                        await apiClient.organizations.linkOrganizationContact({
                            id: orgId,
                            linkOrganizationContactRequest: { contactId },
                        });
                    } catch { linkFailures.push(`Contact #${contactId}`); }
                }
            }
            if (orgId && pendingPropertyIds.length > 0) {
                for (const propertyId of pendingPropertyIds) {
                    try {
                        await apiClient.organizations.linkOrganizationProperty({
                            id: orgId,
                            linkOrganizationPropertyRequest: { propertyId },
                        });
                    } catch { linkFailures.push(`Property #${propertyId}`); }
                }
            }
            if (orgId && pendingClaimIds.length > 0) {
                for (const claimId of pendingClaimIds) {
                    try {
                        await apiClient.claims.linkClaimOrganization({
                            claimId,
                            linkClaimOrganizationRequest: { organizationId: orgId, role: form.orgType },
                        });
                    } catch { linkFailures.push(`Claim #${claimId}`); }
                }
            }
            if (orgId && hasLinks) {
                queryClient.invalidateQueries({ queryKey: ["organization-contacts", orgId] });
                queryClient.invalidateQueries({ queryKey: ["organization-properties", orgId] });
                queryClient.invalidateQueries({ queryKey: ["organization-claims", orgId] });
                queryClient.invalidateQueries({ queryKey: ["contact-organizations"] });
                queryClient.invalidateQueries({ queryKey: ["property-organizations"] });
            }
            if (linkFailures.length > 0) {
                toast({
                    title: "Organization created, but some links failed",
                    description: `Failed to link: ${linkFailures.join(", ")}. You can retry from the organization detail page.`,
                    variant: "destructive",
                });
            }
            onCreated?.();
            handleClose();
        } catch {
            // Error toast handled by mutation's onError callback
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Add Organization</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Name <span className="text-destructive">*</span></Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Organization name" />
                        </div>
                        <div className="space-y-2">
                            <Label>Type <span className="text-destructive">*</span></Label>
                            <Input value={(form.orgType as string) || ""} onChange={(e) => setForm({ ...form, orgType: e.target.value as CreateOrganizationRequest["orgType"] })} placeholder="e.g. Insurance Company" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value || null })} placeholder="(555) 123-4567" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value || null })} placeholder="info@company.com" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Website</Label>
                            <Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value || null })} placeholder="https://..." />
                            {form.website && !isValidWebsite(normalizeWebsite(form.website)) && (
                                <p className="text-xs text-destructive">Please enter a valid URL (e.g. https://example.com)</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Address</Label>
                            <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value || null })} placeholder="123 Main St, City, ST 12345" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} placeholder="Additional notes..." rows={3} />
                    </div>
                    <Separator />
                    <CreateContactsPicker selectedIds={pendingContactIds} onChange={setPendingContactIds} />
                    <CreatePropertiesPicker selectedIds={pendingPropertyIds} onChange={setPendingPropertyIds} />
                    {!hideClaimsPicker && (
                        <CreateClaimsPicker selectedIds={pendingClaimIds} onChange={setPendingClaimIds} />
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!form.name || !form.orgType || isSubmitting || (!!form.website && !isValidWebsite(normalizeWebsite(form.website)))}>
                        {isSubmitting ? "Creating..." : "Create Organization"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
